/**
 * DashboardGestaoController — agregação, KPIs, séries e opções de gráficos.
 *
 * O modelo normalizado vem de application/services/DashboardGestao.js e é
 * recebido por parâmetro; este controller cuida apenas da apresentação.
 */
import { t } from 'i18next';
import DashboardGestaoService from '@/application/services/DashboardGestao';

const { roundDashValue, getCurrentMesKey, addMonths, parsePeriodo } = DashboardGestaoService;

const CHART_COLORS = {
	crimson: '#d81f45',
	muted: '#6b7280',
	ink: '#1a2036',
	line: '#e7e9f0',
	green: '#16a34a',
	red: '#dc2626',
	amber: '#f59e0b',
	blue: '#2f9fe0',
};

const FONT_FAMILY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

function sumField(rows, field) {
	return rows.reduce((total, row) => total + (row[field] || 0), 0);
}

function weightedAverage(rows, field) {
	let weightSum = 0;
	let valueSum = 0;
	rows.forEach(row => {
		const value = row[field];
		if (value === null || value === undefined) return;
		const weight = row.nCenarios > 0 ? row.nCenarios : 1;
		valueSum += value * weight;
		weightSum += weight;
	});
	return weightSum ? valueSum / weightSum : null;
}

function toPercent(value) {
	return value === null ? null : value * 100;
}

function calcPeriodAggregate(rows) {
    if (!rows || !rows.length) return null;
    const nCenarios = sumField(rows, 'nCenarios');
    const publicados = sumField(rows, 'publicados');
    const touchlessCount = sumField(rows, 'touchlessCount');
    const custoReal = weightedAverage(rows, 'custoReal');
    const custoMeta = weightedAverage(rows, 'custoMeta');
	const custoPub = weightedAverage(rows, 'custoPub');
    return {
        nCenarios,
        publicados,
        comSugestaoIA: sumField(rows, 'comSugestaoIA'),
        aprovadosSemCrit: sumField(rows, 'aprovadosSemCrit'),
        semReversao: sumField(rows, 'semReversao'),
        touchlessCount,
        scoreMedio: roundDashValue(weightedAverage(rows, 'scoreMedio'), 'scoreMedio'),
        uplift: roundDashValue(weightedAverage(rows, 'uplift'), 'uplift'),
        touchless: roundDashValue(publicados ? (touchlessCount / publicados) * 100 : 0, 'touchless'),
        leadTime: roundDashValue(weightedAverage(rows, 'leadTimeDias'), 'leadTime'),
        custoReal: roundDashValue(custoReal, 'custoReal'),
        custoMeta: roundDashValue(custoMeta, 'custoMeta'),
		custoPub: roundDashValue(custoPub, 'custoPub'),
        custoVar: roundDashValue(
            custoReal !== null && custoMeta ? ((custoReal - custoMeta) / custoMeta) * 100 : null,
            'custoVar',
        ),
        swapManual: roundDashValue(toPercent(weightedAverage(rows, 'swapManual')), 'swapManualPercent'),
        issuesCriticos: roundDashValue(weightedAverage(rows, 'issuesCriticos'), 'issuesCriticos'),
        issuesMedios: roundDashValue(weightedAverage(rows, 'issuesMedios'), 'issuesMedios'),
        issuesBaixos: roundDashValue(weightedAverage(rows, 'issuesBaixos'), 'issuesBaixos'),
    };
}

function getMonthlyRows(model, filial) {
	const dadosMensais = (model && model.dadosMensais) || {};
	return dadosMensais[filial] || dadosMensais.geral || [];
}

function getCatalog(model) {
	return {
		filiais: (model && model.filiais) || [],
		periodos: (model && model.periodos) || [],
	};
}

function getAvailableRange(rows) {
	const meses = Array.from(new Set((rows || []).map(row => row && row.mes).filter(Boolean))).sort((a, b) => a.localeCompare(b));
	if (!meses.length) return { min: '', max: '' };
	return { min: meses[0], max: meses[meses.length - 1] };
}

function normalizeRange(periodo, availableRange) {
	const range = availableRange || {};
	const min = range.min || '';
	const max = range.max || '';
	if (!min || !max) return { start: '', end: '' };

	const incoming = periodo || {};
	const clampMes = (value, fallback) => {
		const mes = value || fallback;
		return [min, mes, max].sort((a, b) => a.localeCompare(b))[1];
	};
	const hasIncomingRange = !!(incoming.start || incoming.end);

	if (!hasIncomingRange) {
		const currentMes = getCurrentMesKey();
		const defaultEndBase = [max, currentMes].sort((a, b) => a.localeCompare(b))[0];
		const defaultEnd = clampMes(defaultEndBase, max);
		const defaultStart = clampMes(addMonths(defaultEnd, -2), min);
		const [orderedStart, orderedEnd] = [defaultStart, defaultEnd].sort((a, b) => a.localeCompare(b));
		return { start: orderedStart, end: orderedEnd };
	}

	const start = clampMes(incoming.start, min);
	const end = clampMes(incoming.end, max);
	const [orderedStart, orderedEnd] = [start, end].sort((a, b) => a.localeCompare(b));

	
	return { start: orderedStart, end: orderedEnd };
}

function getRangeMonthsCount(range) {
	const parseMes = mes => {
		const parts = String(mes || '').split('-');
		if (parts.length !== 2) return null;
		const year = Number(parts[0]);
		const month = Number(parts[1]);
		if (!year || month < 1 || month > 12) return null;
		return { year, month };
	};

	const start = parseMes(range && range.start);
	const end = parseMes(range && range.end);
	if (!start || !end) return 0;

	return (end.year - start.year) * 12 + (end.month - start.month) + 1;
}

function isRangePeriodo(periodo) {
	return !!(periodo && typeof periodo === 'object' && (Object.prototype.hasOwnProperty.call(periodo, 'start') || Object.prototype.hasOwnProperty.call(periodo, 'end')));
}

/**
 * Filtra linhas mensais pela janela do período selecionado.
 * past: [current-(N-1), current] (mês atual incluso)
 * next: (current, current+N]
 */
function filterRowsByPeriod(rows, periodo) {
	const list = rows || [];
	if (isRangePeriodo(periodo)) {
		const available = getAvailableRange(list);
		const range = {
			start: (periodo && periodo.start) || available.min,
			end: (periodo && periodo.end) || available.max,
		};
		if (!range.start || !range.end) return [];
		return list.filter(row => row.mes >= range.start && row.mes <= range.end);
	}

	const { direction, months } = parsePeriodo(periodo);
	const current = getCurrentMesKey();
	if (direction === 'next') {
		const end = addMonths(current, months);
		return list.filter(row => row.mes > current && row.mes <= end);
	}
	const start = addMonths(current, -(months - 1));
	return list.filter(row => row.mes >= start && row.mes <= current);
}

/**
 * Janela imediatamente anterior à selecionada (mesmo tamanho), para delta dos KPIs.
 * past: [current-(2N-1), current-N]
 * next: usa os N meses passados até o mês atual (precede o início dos próximos)
 *
 */
function filterRowsByPreviousPeriod(rows, periodo) {
	const list = rows || [];
	if (isRangePeriodo(periodo)) {
		const available = getAvailableRange(list);
		const range = {
			start: (periodo && periodo.start) || available.min,
			end: (periodo && periodo.end) || available.max,
		};
		const months = getRangeMonthsCount(range);
		if (!range.start || !range.end || months < 1) return [];
		const end = addMonths(range.start, -1);
		const start = addMonths(end, -(months - 1));
		return list.filter(row => row.mes >= start && row.mes <= end);
	}

	const { direction, months } = parsePeriodo(periodo);
	const current = getCurrentMesKey();
	if (direction === 'next') {
		const start = addMonths(current, -(months - 1));
		return list.filter(row => row.mes >= start && row.mes <= current);
	}
	const end = addMonths(current, -months);
	const start = addMonths(current, -(2 * months - 1));
	return list.filter(row => row.mes >= start && row.mes <= end);
}

const EMPTY_VALUE = '–';

function isNumber(value) {
	return typeof value === 'number' && Number.isFinite(value);
}

function formatMetric(value, digits, signed) {
	if (!isNumber(value)) return EMPTY_VALUE;
	const sign = signed && value > 0 ? '+' : '';
	return sign + value.toFixed(digits);
}

function calcDelta(current, previous) {
	if (!isNumber(current) || !isNumber(previous)) return null;
	return roundDashValue(current - previous, 'uplift');
}

/** Delta de um campo entre períodos; null quando não há período anterior para comparar. */
function calcFieldDelta(currentPeriod, previousPeriod, field) {
	if (!previousPeriod) return null;
	return calcDelta(currentPeriod[field], previousPeriod[field]);
}

function buildKpiList(currentPeriod, previousPeriod) {
	if (!currentPeriod) return [];
	return [
		{
			icon: 'chart-bar',
			label: t('dashboard_gestao_kpi_score_medio'),
			hint: t('dashboard_gestao_kpi_score_medio_tooltip'),
			unit: 'pts',
			goodDir: 'up',
			value: formatMetric(currentPeriod.scoreMedio, 0),
			delta: calcFieldDelta(currentPeriod, previousPeriod, 'scoreMedio'),
		},
		{
			icon: 'chart-bar',
			label: t('dashboard_gestao_kpi_uplift_ia'),
			hint: t('dashboard_gestao_kpi_uplift_ia_tooltip'),
			unit: 'pts',
			goodDir: 'up',
			value: formatMetric(currentPeriod.uplift, 1, true),
			delta: calcFieldDelta(currentPeriod, previousPeriod, 'uplift'),
		},
		{
			icon: 'check-circle',
			label: t('dashboard_gestao_kpi_publicados_sem_edicao'),
			hint: t('dashboard_gestao_kpi_publicados_sem_edicao_tooltip'),
			unit: '%',
			goodDir: 'up',
			value: formatMetric(currentPeriod.touchless, 0),
			delta: calcFieldDelta(currentPeriod, previousPeriod, 'touchless'),
		},
		{
			icon: 'clock',
			label: t('dashboard_gestao_kpi_lead_time_planejamento'),
			hint: t('dashboard_gestao_kpi_lead_time_planejamento_tooltip'),
			unit: 'dias',
			goodDir: 'down',
			value: formatMetric(currentPeriod.leadTime, 1),
			delta: calcFieldDelta(currentPeriod, previousPeriod, 'leadTime'),
		},
		{
			icon: 'dollar-sign',
			label: t('dashboard_gestao_kpi_custo_medio_vs_meta'),
			hint: t('dashboard_gestao_kpi_custo_medio_vs_meta_tooltip'),
			unit: '%',
			goodDir: 'down',
			value: formatMetric(currentPeriod.custoVar, 1, true),
			delta: calcFieldDelta(currentPeriod, previousPeriod, 'custoVar'),
		},
	];
}

function getEvolutionChartData(cur) {
	return {
		categories: cur.map(r => r.label),
		series: [
			{ name: 'Cenários', type: 'bar', data: cur.map(r => r.nCenarios) },
			{ name: 'Score médio', type: 'line', data: cur.map(r => r.scoreMedio) },
		],
	};
}

function getCostChartData(cur) {
	return {
		categories: cur.map(r => r.label),
		series: [
			{ name: 'PU Cenários', data: cur.map(r => r.custoReal) },
			{ name: 'Meta', data: cur.map(r => r.custoMeta) },
			{ name: 'PU Publicados', data: cur.map(r => r.custoPub) },
		],
	};
}

function getIssuesChartData(cur) {
	return {
		categories: cur.map(r => r.label),
		series: [
			{ name: 'Críticos', data: cur.map(r => r.issuesCriticos) },
			{ name: 'Médios', data: cur.map(r => r.issuesMedios) },
			{ name: 'Baixos', data: cur.map(r => r.issuesBaixos) },
		],
	};
}
function getConfidenceFunnel(aggregate) {
    if (!aggregate) return [];
    const funnel = [
        { label: t('Cenários_gerados'), v: aggregate.nCenarios || 0, c: CHART_COLORS.blue, parent: null },
        { label: t('Com_sugestão_da_IA_aplicada'), v: aggregate.comSugestaoIA || 0, c: '#4aa9e3', parent: 'nCenarios' },
        { label: t('Sem_reversão_de_troca_da_IA'), v: aggregate.semReversao || 0, c: CHART_COLORS.green, parent: 'nCenarios' },
        { label: t('Cenários_Publicados'), v: aggregate.publicados || 0, c: '#5fb56b', parent: 'nCenarios' },
        { label: t('Publicados_sem_issue_crítica'), v: aggregate.aprovadosSemCrit || 0, c: '#5fb56b', parent: 'publicados' },
        { label: t('Publicados_sem_edição'), v: aggregate.touchlessCount || 0, c: '#0f8a3c', parent: 'publicados' },
    ];

    const fMax = funnel[0].v || 1;
    return funnel.map((step, i) => {
        let conv = null;
        if (step.parent) {
            const parentValue = aggregate[step.parent] || 0;
            conv = parentValue
                ? (step.v / parentValue) * 100
                : 0;
        }
        return {
            ...step,
            //width: roundDashValue((step.v / fMax) * 100, 'width'),
			width: roundDashValue(conv === null ? 100 : conv,'width'),
            conv: conv === null
                ? null
                : roundDashValue(conv, 'conv'),
        };
    });
}

function getFilialRanking(model, periodo) {
	const filiais = (model && model.filiais) || [];
	return filiais
		.map(f => {
			const agg = calcPeriodAggregate(filterRowsByPeriod(getMonthlyRows(model, f.id), periodo));
			if (!agg) return null;
			return {
				id: f.id,
				nome: f.nome,
				score: agg.scoreMedio,
				touchless: agg.touchless,
				swap: agg.swapManual,
				lead: agg.leadTime,
				n: agg.nCenarios,
			};
		})
		.filter(Boolean)
		.sort(compareByScoreDesc);
}

function buildDashboardExportTabs({ cur = [], kpis = [], ranking = [], funnel = [], headerDetails = [] } = {}) {
	const withHeader = tab => ({ ...tab, headerDetails });
	const numericKpiValue = value => {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : null;
	};

	const resumoData = kpis.map(kpi => ({
		indicador: kpi.label,
		valor: numericKpiValue(kpi.value),
		unidade: kpi.unit,
		variacao: kpi.delta,
	}));
	const mensalData = cur.map(row => ({
		mes: row.label,
		cenarios: row.nCenarios,
		publicados: row.publicados,
		comSugestaoIA: row.comSugestaoIA,
		semReversao: row.semReversao,
		publicadosSemCriticos: row.aprovadosSemCrit,
		publicadosSemEdicao: row.touchlessCount,
		publicadosSemEdicaoPercentual: roundDashValue((row.touchlessRate || 0) * 100, 'touchless'),
		scoreMedio: row.scoreMedio,
		uplift: row.uplift,
		swapManual: roundDashValue(row.swapManual === null ? null : row.swapManual * 100, 'swapManualPercent'),
		custoReal: row.custoReal,
		custoMeta: row.custoMeta,
		custoPub: row.custoPub,
		leadTime: row.leadTimeDias,
		issuesCriticos: row.issuesCriticos,
		issuesMedios: row.issuesMedios,
		issuesBaixos: row.issuesBaixos,
	}));
	const rankingData = ranking.map((row, index) => ({
		posicao: index + 1,
		filial: row.nome,
		cenarios: row.n,
		score: row.score,
		publicadosSemEdicao: row.touchless,
		swapManual: row.swap,
		leadTime: row.lead,
	}));
	const funnelData = funnel.map(step => ({
		etapa: step.label,
		quantidade: step.v,
		conversao: step.conv,
	}));

	return [
		withHeader({
			tabName: t('dashboard_gestao_export_tab_resumo'),
			jsonData: resumoData,
			colDetails: [
				{ header: t('dashboard_gestao_export_coluna_indicador'), key: 'indicador' },
				{ header: t('dashboard_gestao_export_coluna_valor'), key: 'valor' },
				{ header: t('dashboard_gestao_export_coluna_unidade'), key: 'unidade' },
				{ header: t('dashboard_gestao_export_coluna_variacao'), key: 'variacao' },
			],
		}),
		withHeader({
			tabName: t('dashboard_gestao_export_tab_mensal'),
			jsonData: mensalData,
			colDetails: [
				{ header: t('month'), key: 'mes' },
				{ header: t('dashboard_gestao_coluna_cenarios'), key: 'cenarios' },
				{ header: t('dashboard_gestao_export_coluna_publicados'), key: 'publicados' },
				{ header: t('dashboard_gestao_export_coluna_sugestao_ia'), key: 'comSugestaoIA' },
				{ header: t('dashboard_gestao_export_coluna_sem_reversao'), key: 'semReversao' },
				{ header: t('dashboard_gestao_export_coluna_sem_criticos'), key: 'publicadosSemCriticos' },
				{ header: t('dashboard_gestao_export_coluna_sem_edicao_qtd'), key: 'publicadosSemEdicao' },
				{ header: t('dashboard_gestao_export_coluna_sem_edicao_pct'), key: 'publicadosSemEdicaoPercentual' },
				{ header: t('dashboard_gestao_coluna_score'), key: 'scoreMedio' },
				{ header: t('dashboard_gestao_export_coluna_uplift'), key: 'uplift' },
				{ header: t('dashboard_gestao_export_coluna_swap_pct'), key: 'swapManual' },
				{ header: t('dashboard_gestao_export_coluna_custo_real'), key: 'custoReal' },
				{ header: t('dashboard_gestao_export_coluna_custo_meta'), key: 'custoMeta' },
				{ header: t('dashboard_gestao_export_coluna_custo_Pub'), key: 'custoPub' },
				{ header: t('dashboard_gestao_export_coluna_lead_time_dias'), key: 'leadTime' },
				{ header: t('dashboard_gestao_legenda_criticos'), key: 'issuesCriticos' },
				{ header: t('dashboard_gestao_legenda_medios'), key: 'issuesMedios' },
				{ header: t('dashboard_gestao_legenda_baixos'), key: 'issuesBaixos' },
			],
		}),
		withHeader({
			tabName: t('dashboard_gestao_export_tab_ranking'),
			jsonData: rankingData,
			colDetails: [
				{ header: t('dashboard_gestao_export_coluna_posicao'), key: 'posicao' },
				{ header: t('dashboard_gestao_coluna_filial'), key: 'filial' },
				{ header: t('dashboard_gestao_coluna_cenarios'), key: 'cenarios' },
				{ header: t('dashboard_gestao_coluna_score'), key: 'score' },
				{ header: t('dashboard_gestao_export_coluna_sem_edicao_pct'), key: 'publicadosSemEdicao' },
				{ header: t('dashboard_gestao_export_coluna_swap_pct'), key: 'swapManual' },
				{ header: t('dashboard_gestao_export_coluna_lead_time_dias'), key: 'leadTime' },
			],
		}),
		withHeader({
			tabName: t('dashboard_gestao_export_tab_funil'),
			jsonData: funnelData,
			colDetails: [
				{ header: t('dashboard_gestao_export_coluna_etapa'), key: 'etapa' },
				{ header: t('dashboard_gestao_export_coluna_quantidade'), key: 'quantidade' },
				{ header: t('dashboard_gestao_export_coluna_conversao'), key: 'conversao' },
			],
		}),
	];
}

/** Filiais sem score apurado ficam no fim do ranking. */
function compareByScoreDesc(a, b) {
	if (!isNumber(a.score) && !isNumber(b.score)) return 0;
	if (!isNumber(a.score)) return 1;
	if (!isNumber(b.score)) return -1;
	return b.score - a.score;
}

function getHeatmapColor(norm, base) {
	const safeNorm = isNumber(norm) ? norm : 0;
	const alpha = (0.12 + Math.max(0, Math.min(1, safeNorm)) * 0.55).toFixed(2);
	return `rgba(${base}, ${alpha})`;
}

function isKpiDeltaNeutral(delta) {
	return !isNumber(delta) || Math.abs(delta) < 0.05;
}

function isKpiImproving(kpi) {
	if (!isNumber(kpi.delta)) return false;
	return kpi.goodDir === 'up' ? kpi.delta > 0 : kpi.delta < 0;
}

/** Direção literal da seta: reflete o sinal do delta, não se ele é bom ou ruim para o KPI. */
function getKpiDeltaDirection(kpi) {
	return isNumber(kpi.delta) && kpi.delta < 0 ? 'down' : 'up';
}

function getKpiDeltaColor(kpi) {
	if (isKpiDeltaNeutral(kpi.delta)) return CHART_COLORS.muted;
	return isKpiImproving(kpi) ? CHART_COLORS.green : CHART_COLORS.red;
}

function formatKpiDelta(kpi) {
	if (!isNumber(kpi.delta)) return EMPTY_VALUE;
	if (isKpiDeltaNeutral(kpi.delta)) return 'estável';
	const suffix = kpi.unit === '%' ? ' p.p.' : '';
	const sign = kpi.delta > 0 ? '+' : '';
	return `${sign}${kpi.delta.toFixed(1)}${suffix}`;
}

function getEvolutionChartOptions(cur) {
	const chartData = getEvolutionChartData(cur);
	return {
		chart: {
			type: 'line',
			fontFamily: FONT_FAMILY,
			toolbar: { show: false },
			animations: { enabled: true },
		},
		colors: ['#b3b3b3ff', CHART_COLORS.crimson],
		stroke: { width: [0, 2.5], curve: 'straight' },
		fill: { type: ['solid', 'solid'], opacity: [1, 1] },
		plotOptions: {
			bar: { columnWidth: '45%', borderRadius: 5, borderRadiusApplication: 'end' },
		},
		states: {
			hover: {
				filter: {
					type: 'darken',
					value: 0.2,
				},
			},
		},
		markers: { size: [0, 3], colors: [CHART_COLORS.crimson], strokeColors: CHART_COLORS.crimson },
		dataLabels: { enabled: false },
		labels: chartData.categories,
		xaxis: {
			labels: { style: { colors: CHART_COLORS.muted, fontSize: '11px' } },
			axisBorder: { show: false },
			axisTicks: { show: false },
		},
		yaxis: [
			{
				seriesName: t('Cenários'),
				labels: { style: { colors: CHART_COLORS.muted, fontSize: '11px' } },
				axisBorder: { show: false },
				axisTicks: { show: false },
			},
			{
				seriesName: t('Score_médio'),
				opposite: true,
				labels: { style: { colors: CHART_COLORS.muted, fontSize: '11px' } },
				axisBorder: { show: false },
				axisTicks: { show: false },
			},
		],
		legend: { show: true, position: 'top', fontSize: '12px', labels: { colors: CHART_COLORS.ink } },
		tooltip: {
			theme: 'light',
			style: { fontSize: '12px', borderRadius: 10 },
			shared: true,
			intersect: false,
		},
		grid: {
			borderColor: CHART_COLORS.line,
			strokeDashArray: 0,
			xaxis: { lines: { show: false } },
			yaxis: { lines: { show: true } },
		},
	};
}

function getCostChartOptions(cur) {
	const chartData = getCostChartData(cur);
	return {
		chart: {
			type: 'area',
			fontFamily: FONT_FAMILY,
			toolbar: { show: false },
			animations: { enabled: true },
		},
		colors: [CHART_COLORS.crimson, CHART_COLORS.muted, CHART_COLORS.blue],
		stroke: { width: [2.5, 1.5, 2], dashArray: [0, 4, 0], curve: 'straight' },
		fill: {
			type: 'gradient',
			gradient: { shadeIntensity: 1, opacityFrom: 0.25, opacityTo: 0, stops: [0, 100] },
		},
		dataLabels: { enabled: false },
		labels: chartData.categories,
		xaxis: {
			labels: { style: { colors: CHART_COLORS.muted, fontSize: '10px' } },
			axisBorder: { show: false },
			axisTicks: { show: false },
		},
		yaxis: {
			labels: {
				style: { colors: CHART_COLORS.muted, fontSize: '10px' },
				formatter: v => 'R$ ' + Number(v).toFixed(2),
			},
			axisBorder: { show: false },
			axisTicks: { show: false },
		},
		legend: { show: true, position: 'top', fontSize: '12px', labels: { colors: CHART_COLORS.ink } },
		tooltip: {
			theme: 'light',
			style: { fontSize: '12px', borderRadius: 10 },
			y: { formatter: v => 'R$ ' + Number(v).toFixed(2) },
		},
		grid: {
			borderColor: CHART_COLORS.line,
			strokeDashArray: 0,
			xaxis: { lines: { show: false } },
			yaxis: { lines: { show: true } },
		},
		markers: { size: [2.5, 0, 2.5], colors: [CHART_COLORS.crimson, CHART_COLORS.muted, CHART_COLORS.blue], strokeColors: [CHART_COLORS.crimson, CHART_COLORS.muted, CHART_COLORS.blue] },
	};
}

function getIssuesChartOptions(cur) {
	const chartData = getIssuesChartData(cur);
	return {
		chart: {
			type: 'bar',
			stacked: true,
			fontFamily: FONT_FAMILY,
			toolbar: { show: false },
			animations: { enabled: true },
		},
		colors: [CHART_COLORS.red, CHART_COLORS.amber, '#e9c94a'],
		plotOptions: {
			bar: { columnWidth: '45%', borderRadius: 5, borderRadiusApplication: 'end' },
		},
		dataLabels: { enabled: false },
		labels: chartData.categories,
		xaxis: {
			labels: { style: { colors: CHART_COLORS.muted, fontSize: '11px' } },
			axisBorder: { show: false },
			axisTicks: { show: false },
		},
		yaxis: {
			labels: { style: { colors: CHART_COLORS.muted, fontSize: '11px' } },
			axisBorder: { show: false },
			axisTicks: { show: false },
		},
		legend: { show: false },
		tooltip: { theme: 'light', style: { fontSize: '12px', borderRadius: 10 } },
		grid: {
			borderColor: CHART_COLORS.line,
			strokeDashArray: 0,
			xaxis: { lines: { show: false } },
			yaxis: { lines: { show: true } },
		},
	};
}

export default {
	CHART_COLORS,
	getCatalog,
	getAvailableRange,
	normalizeRange,
	getMonthlyRows,
	filterRowsByPeriod,
	filterRowsByPreviousPeriod,
	calcPeriodAggregate,
	buildKpiList,
	getEvolutionChartData,
	getCostChartData,
	getIssuesChartData,
	getConfidenceFunnel,
	getFilialRanking,
	buildDashboardExportTabs,
	getHeatmapColor,
	isKpiDeltaNeutral,
	isKpiImproving,
	getKpiDeltaDirection,
	getKpiDeltaColor,
	formatKpiDelta,
	getEvolutionChartOptions,
	getCostChartOptions,
	getIssuesChartOptions,
};
